// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd
import { Button, Dropdown, type MenuProps } from "antd";
import {
  AudioMutedOutlined,
  AudioOutlined,
  CheckOutlined,
  DownOutlined,
  EditOutlined,
  HighlightOutlined,
  PhoneOutlined,
  SettingOutlined,
  FundProjectionScreenOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  UserAddOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FlipCameraIcon } from "./FlipCameraIcon";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnnotationOverlay, type AnnotationConfig } from "./AnnotationOverlay";
import type { CallQuality, CallState, VideoQuality } from "../rtc";
import type { BgMode } from "../bgeffect";
import { qualityMenuGroup, backgroundMenuGroup } from "./qualityMenu";
import { usePipDrag } from "./usePipDrag";
import { useSpeaking } from "../speaking";
import { cameraLabel, micLabel } from "../devices";

// CallView is the dedicated full-screen call surface (Messenger-style): incoming
// prompt, outgoing "calling…", and the in-call screen with all controls (mute,
// camera, back-to-messages, hang up). The remote/local <video> elements stay mounted
// for the whole call (audio plays even when minimised), so minimising never drops the
// stream. Audio-only calls show the peer's monogram instead of video.

// attachStream points a <video> at a MediaStream (idempotent).
function attachStream(el: HTMLVideoElement | null, stream: MediaStream | null): void {
  if (!el) return;
  if (el.srcObject !== stream) el.srcObject = stream;
  if (stream) void el.play().catch(() => undefined);
}

// reattach forcibly re-points a <video> at its stream when its TRACKS change (a swap
// within the same stream — e.g. screen-share ↔ camera). attachStream's same-object guard
// would skip it, leaving the element frozen on the last (screen) frame; clearing first
// drops that frame, and we only re-attach if there's a live video track.
function reattach(el: HTMLVideoElement | null, stream: MediaStream | null): void {
  if (!el) return;
  const hasVideo = !!stream?.getVideoTracks().some((t) => t.enabled);
  el.srcObject = null;
  el.srcObject = hasVideo ? stream : null;
  if (hasVideo) void el.play().catch(() => undefined);
}

export function CallView({
  state,
  video,
  connType,
  linkQuality,
  verification,
  transferredAway,
  sendingVideo,
  peerName,
  incomingVideo,
  muted,
  cameraOff,
  minimized,
  remoteStream,
  localStream,
  mics,
  cameras,
  currentMic,
  currentCamera,
  onSelectMic,
  onSelectCamera,
  onFlipCamera,
  quality,
  onSelectQuality,
  background,
  onSelectBackground,
  onAccept,
  onDecline,
  onHangup,
  onToggleMute,
  onToggleCamera,
  onShareScreen,
  sharingScreen,
  peerPresenting,
  peerMuted,
  onMinimize,
  onRestore,
  onAddPerson,
  outputs,
  currentOutput,
  onSelectOutput,
  annotation,
  selfAnnotation,
  annotationAllowed,
  onToggleAnnotation,
  onOpenWhiteboard,
}: {
  readonly state: CallState;
  readonly video: boolean;
  readonly connType: "direct" | "relay" | "";
  readonly linkQuality: CallQuality | null;
  readonly verification: { glyph: string; name: string }[] | null;
  readonly transferredAway: boolean;
  readonly sharingScreen: boolean; // THIS device is sharing its screen
  readonly peerPresenting: boolean; // the peer is sharing their screen
  readonly peerMuted: boolean; // the peer's mic is muted
  readonly sendingVideo: boolean;
  readonly peerName: string;
  readonly incomingVideo: boolean;
  readonly muted: boolean;
  readonly cameraOff: boolean;
  readonly minimized: boolean;
  readonly remoteStream: MediaStream | null;
  readonly localStream: MediaStream | null;
  readonly mics: MediaDeviceInfo[];
  readonly cameras: MediaDeviceInfo[];
  readonly currentMic?: string;
  readonly currentCamera?: string;
  readonly onSelectMic: (deviceId: string) => void;
  readonly onSelectCamera: (deviceId: string) => void;
  readonly onFlipCamera: () => void;
  readonly quality: VideoQuality;
  readonly onSelectQuality: (q: VideoQuality) => void;
  readonly background: BgMode;
  readonly onSelectBackground: (m: BgMode) => void;
  readonly onAccept: (video: boolean) => void;
  readonly onDecline: () => void;
  readonly onHangup: () => void;
  readonly onToggleMute: () => void;
  readonly onToggleCamera: () => void;
  readonly onShareScreen: () => void;
  readonly onMinimize: () => void;
  readonly onRestore: () => void;
  readonly onAddPerson: () => void;
  readonly outputs: { id: string; label: string }[];
  readonly currentOutput: string | null;
  readonly onSelectOutput: (id: string) => void;
  // When set and the peer is sharing their screen, an annotation overlay is mounted over
  // the shared video (WB-007). null/undefined = no annotation surface.
  readonly annotation?: AnnotationConfig | null;
  // The presenter's own-share overlay (WB-008): always drawable; mounted over MY screen
  // when I'm sharing and it's the main view.
  readonly selfAnnotation?: AnnotationConfig | null;
  readonly annotationAllowed?: boolean; // do I (presenter) currently allow others to annotate
  readonly onToggleAnnotation?: () => void;
  readonly onOpenWhiteboard?: () => void; // open the Mode B shared whiteboard (WB-011)
}) {
  // Callback refs attach the stream SYNCHRONOUSLY when the <video> mounts (and when
  // the stream changes) — not in an effect. This kills the race where the caller's
  // getUserMedia resolves before the call screen mounts, which made the self-view
  // (and remote video) appear only intermittently.
  const remoteElRef = useRef<HTMLVideoElement | null>(null);
  const localElRef = useRef<HTMLVideoElement | null>(null);
  const pip = usePipDrag(onRestore); // draggable minimised window (tap = restore)
  // In-call (full screen) UX: the small video is a draggable PiP; tapping it SWAPS which
  // stream is full-screen; tapping the background toggles the auto-hiding controls.
  const [swapped, setSwapped] = useState(false); // true → MY video is the main one, peer in the PiP
  const [controlsShown, setControlsShown] = useState(true);
  const [showVerify, setShowVerify] = useState(false); // the call-verification emoji panel
  const [swapAnim, setSwapAnim] = useState(false); // brief transition when the main video swaps
  const firstSwapRef = useRef(true);
  const localPip = usePipDrag(() => setSwapped((s) => !s));
  const bindRemote = useCallback((el: HTMLVideoElement | null) => { remoteElRef.current = el; attachStream(el, remoteStream); }, [remoteStream]);
  const bindLocal = useCallback((el: HTMLVideoElement | null) => { localElRef.current = el; attachStream(el, localStream); }, [localStream]);
  // A remote video track can arrive AFTER the first attach (the second ontrack, or a
  // mid-call upgrade) and some browsers won't auto-render it — re-attach on addtrack.
  useEffect(() => {
    const s = remoteStream;
    if (!s) return;
    const onChange = () => reattach(remoteElRef.current, s);
    s.addEventListener("addtrack", onChange);
    s.addEventListener("removetrack", onChange);
    return () => {
      s.removeEventListener("addtrack", onChange);
      s.removeEventListener("removetrack", onChange);
    };
  }, [remoteStream]);
  // Same for OUR self-view: when screen-share ends and the camera is restored (or removed),
  // re-point the element so it doesn't keep the last screen frame (stretched, since the
  // object-fit:contain class is gone).
  useEffect(() => {
    const s = localStream;
    if (!s) return;
    const onChange = () => reattach(localElRef.current, s);
    s.addEventListener("addtrack", onChange);
    s.addEventListener("removetrack", onChange);
    return () => {
      s.removeEventListener("addtrack", onChange);
      s.removeEventListener("removetrack", onChange);
    };
  }, [localStream]);

  // Web audio-output selection: point the element that plays the call audio at the chosen
  // sink. No-op where setSinkId is absent (native WebView routes via the OS plugin instead).
  useEffect(() => {
    if (!currentOutput) return;
    const el = remoteElRef.current as (HTMLVideoElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    void el?.setSinkId?.(currentOutput).catch(() => undefined);
  }, [currentOutput, remoteStream]);

  // Active-speaker glow (hooks before the early return): tag the peer + self by energy.
  const { speaking } = useSpeaking({ __peer: remoteStream, __self: localStream });
  const peerSpeaking = speaking.includes("__peer");
  const selfSpeaking = speaking.includes("__self");

  // Auto-hide the controls after a few idle seconds, but ONLY in a connected video call —
  // an audio call (no video to look at) or an incoming/ringing call keeps them pinned so the
  // mute/accept/decline buttons are always reachable.
  const autoHideEligible = state === "connected" && video && matchMedia("(pointer: coarse)").matches; // touch only
  useEffect(() => {
    if (!autoHideEligible) {
      setControlsShown(true);
      return;
    }
    if (!controlsShown) return;
    const t = setTimeout(() => setControlsShown(false), 10000);
    return () => clearTimeout(t);
  }, [autoHideEligible, controlsShown]);
  // Reset the swap whenever the call drops out of a video state.
  useEffect(() => {
    if (!video) setSwapped(false);
  }, [video]);
  // Briefly animate the main view when which stream is full-screen swaps, so the change reads.
  useEffect(() => {
    if (firstSwapRef.current) {
      firstSwapRef.current = false;
      return;
    }
    setSwapAnim(true);
    const t = setTimeout(() => setSwapAnim(false), 360);
    return () => clearTimeout(t);
  }, [swapped]);

  if (state === "idle" || state === "ended") return null;

  const incoming = state === "incoming";
  const ringing = state === "ringing";
  const monogram = peerName.replace(/^@/, "").charAt(0).toUpperCase() || "?";
  let statusText: string;
  if (transferredAway) statusText = "Moving to your other device…";
  else if (incoming) statusText = `Incoming ${incomingVideo ? "video " : ""}call`;
  else if (ringing) statusText = "Calling…";
  else if (state === "connecting") statusText = "Connecting…";
  else statusText = "Connected";

  // The media elements are ALWAYS mounted while a call exists (so audio keeps
  // playing and srcObject survives minimise); visibility is CSS-driven.
  const media = (
    <>
      <video ref={bindRemote} autoPlay playsInline className={`call-remote${peerPresenting ? " call-screen" : ""}${peerSpeaking ? " call-speaking" : ""}`} style={{ display: video ? "block" : "none" }}>
        <track kind="captions" />
      </video>
      {peerPresenting && <div className="call-screen-badge">🖥 {peerName.replace(/^@/, "").split(":")[0]} is sharing their screen</div>}
      {peerMuted && (
        <div className="call-muted-badge">
          <AudioMutedOutlined /> {peerName.replace(/^@/, "").split(":")[0]} is muted
        </div>
      )}
      <video ref={bindLocal} autoPlay playsInline muted className={`call-local${sharingScreen ? " call-screen" : " call-self"}${selfSpeaking ? " call-speaking" : ""}`} style={{ display: sendingVideo ? "block" : "none" }}>
        <track kind="captions" />
      </video>
    </>
  );

  // Full-screen VIDEO layout (in-call): one stream is the main (full-bleed `call-remote`), the
  // other is the draggable PiP (`call-local`); `swapped` flips which is which. The PiP video
  // carries the drag handlers + position (tap = swap); the MAIN video's tap toggles the chrome.
  const toggleControls = () => setControlsShown((s) => !s);
  const dragProps = { onPointerDown: localPip.onPointerDown, onPointerMove: localPip.onPointerMove, onPointerUp: localPip.onPointerUp, onPointerCancel: localPip.onPointerCancel };
  const peerShort = peerName.replace(/^@/, "").split(":")[0];
  const videoMedia = (
    <>
      <video
        ref={(el) => { bindRemote(el); if (swapped) localPip.ref(el); }}
        autoPlay
        playsInline
        className={`${swapped ? "call-local" : "call-remote"}${peerPresenting ? " call-screen" : ""}${peerSpeaking ? " call-speaking" : ""}`}
        style={{ display: video ? "block" : "none", ...(swapped ? localPip.style : {}) }}
        onClick={swapped ? undefined : toggleControls}
        {...(swapped ? dragProps : {})}
      >
        <track kind="captions" />
      </video>
      {!swapped && peerPresenting && <div className="call-screen-badge">🖥 {peerShort} is sharing their screen</div>}
      {!swapped && peerMuted && (
        <div className="call-muted-badge">
          <AudioMutedOutlined /> {peerShort} is muted
        </div>
      )}
      <video
        ref={(el) => { bindLocal(el); if (!swapped) localPip.ref(el); }}
        autoPlay
        playsInline
        muted
        className={`${swapped ? "call-remote" : "call-local"}${sharingScreen ? " call-screen" : " call-self"}${selfSpeaking ? " call-speaking" : ""}`}
        style={{ display: sendingVideo ? "block" : "none", ...(swapped ? {} : localPip.style) }}
        onClick={swapped ? toggleControls : undefined}
        {...(swapped ? {} : dragProps)}
      >
        <track kind="captions" />
      </video>
    </>
  );

  // Device picker (mic + camera) — switches the sent track without renegotiation.
  const deviceItem = (d: MediaDeviceInfo, selected: boolean, label: string, onClick: () => void) => ({
    key: d.deviceId,
    label,
    icon: selected ? <CheckOutlined /> : <span style={{ display: "inline-block", width: 12 }} />,
    onClick,
  });
  const deviceMenu: MenuProps = {
    items: [
      { key: "mic-group", type: "group", label: "Microphone", children: mics.map((d, i) => deviceItem(d, d.deviceId === currentMic, micLabel(d, i), () => onSelectMic(d.deviceId))) },
      ...(cameras.length
        ? [{ key: "cam-group", type: "group" as const, label: "Camera", children: cameras.map((d, i) => deviceItem(d, d.deviceId === currentCamera, cameraLabel(d, i), () => onSelectCamera(d.deviceId))) }]
        : []),
      qualityMenuGroup(quality, onSelectQuality),
      ...(cameras.length ? [backgroundMenuGroup(background, onSelectBackground)] : []),
    ],
  };

  if (minimized) {
    // Video call → a small, draggable picture-in-picture window (chat is usable
    // underneath). Audio-only → the media stays mounted (hidden) for sound, with a pill.
    if (video) {
      return (
        <div
          ref={pip.ref}
          className="call-pip"
          style={pip.style}
          onPointerDown={pip.onPointerDown}
          onPointerMove={pip.onPointerMove}
          onPointerUp={pip.onPointerUp}
          onPointerCancel={pip.onPointerCancel}
          title="Drag · tap to return to the call"
        >
          {media}
          <button
            className="call-pip-hangup"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onHangup();
            }}
            title="Hang up"
          >
            <PhoneOutlined rotate={135} />
          </button>
        </div>
      );
    }
    return (
      <>
        {media}
        <button className="call-mini" onClick={onRestore} title="Return to call">
          <PhoneOutlined /> <span>In call · {peerName}</span>
        </button>
      </>
    );
  }

  return (
    <div className={`call-view ${video ? "call-view-video" : "call-view-audio"}${controlsShown ? "" : " controls-hidden"}${swapAnim ? " call-swapping" : ""}`}>
      {video ? videoMedia : media}
      {annotation && peerPresenting && !swapped && (
        <AnnotationOverlay
          videoRef={remoteElRef}
          author={annotation.author}
          boardId={annotation.boardId}
          color={annotation.color}
          enabled={annotation.enabled}
          sendOp={annotation.sendOp}
          register={annotation.register}
        />
      )}
      {/* The presenter's own-share overlay: drawable only while MY screen is the main view
          (swapped → local is full-bleed), so the anchor matches the video box. */}
      {selfAnnotation && sharingScreen && swapped && (
        <AnnotationOverlay
          videoRef={localElRef}
          author={selfAnnotation.author}
          boardId={selfAnnotation.boardId}
          color={selfAnnotation.color}
          enabled={selfAnnotation.enabled}
          sendOp={selfAnnotation.sendOp}
          register={selfAnnotation.register}
        />
      )}
      {!video && (
        <div className="call-face">
          <div className="call-monogram">{monogram === "?" ? <UserOutlined /> : monogram}</div>
        </div>
      )}
      {!incoming && !ringing && (
        <div className="call-topbar">
          <Button shape="circle" type="text" className="call-top-btn" icon={<DownOutlined />} onClick={onMinimize} title="Minimise (back to chat)" />
          <div className="call-topbar-right">
            <Button shape="circle" type="text" className="call-top-btn" icon={<UserAddOutlined />} onClick={onAddPerson} title="Add people to the call" />
            {outputs.length > 1 && (
              <Dropdown
                placement="bottomRight"
                trigger={["click"]}
                menu={{
                  items: outputs.map((o) => ({
                    key: o.id,
                    label: o.label,
                    icon: o.id === currentOutput ? <CheckOutlined /> : <span style={{ display: "inline-block", width: 12 }} />,
                    onClick: () => onSelectOutput(o.id),
                  })),
                }}
              >
                <Button shape="circle" type="text" className="call-top-btn" icon={<SoundOutlined />} title="Audio output" />
              </Dropdown>
            )}
          </div>
        </div>
      )}
      <div className="call-head">
        <div className="call-peer">{peerName}</div>
        <div className="call-status">
          {statusText}
          {state === "connected" && connType && (
            <span className={`call-path call-path-${connType}`} title={connType === "direct" ? "Peer-to-peer — media is not relayed through the server" : "Relayed through the TURN server (no direct path available)"}>
              {connType === "direct" ? "Direct" : "Relayed"}
            </span>
          )}
          {state === "connected" && linkQuality && <CallQualityChip q={linkQuality} />}
        </div>
        {state === "connected" && verification && verification.length > 0 && (
          <button type="button" className="call-verify-chip" onClick={() => setShowVerify((v) => !v)} title="Verify this call is end-to-end secure">
            <SafetyCertificateOutlined /> {verification.map((e) => e.glyph).join(" ")}
          </button>
        )}
      </div>

      {showVerify && verification && (
        <div className="call-verify-panel" onClick={() => setShowVerify(false)}>
          <div className="call-verify-card" onClick={(e) => e.stopPropagation()}>
            <SafetyCertificateOutlined style={{ fontSize: 28, color: "#19c37d" }} />
            <div className="call-verify-title">This call is end-to-end encrypted</div>
            <div className="call-verify-emoji">
              {verification.map((e, i) => (
                <div key={`${i}-${e.name}`} className="call-verify-em">
                  <div className="call-verify-glyph">{e.glyph}</div>
                  <div className="call-verify-name">{e.name}</div>
                </div>
              ))}
            </div>
            <div className="call-verify-hint">
              If {peerShort} sees these same emoji, no one is intercepting the call. They change every call.
            </div>
            <Button onClick={() => setShowVerify(false)}>Got it</Button>
          </div>
        </div>
      )}

      <div className="call-controls">
        {incoming ? (
          <>
            <Button shape="circle" size="large" danger type="primary" icon={<PhoneOutlined rotate={135} />} onClick={onDecline} title="Decline" />
            {incomingVideo && (
              <Button shape="circle" size="large" icon={<VideoCameraOutlined />} onClick={() => onAccept(true)} title="Accept with video" />
            )}
            <Button shape="circle" size="large" type="primary" icon={<PhoneOutlined />} onClick={() => onAccept(false)} title="Accept" />
          </>
        ) : (
          <>
            <Button shape="circle" size="large" icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />} onClick={onToggleMute} title={muted ? "Unmute" : "Mute"} />
            <Button
              shape="circle"
              size="large"
              icon={sendingVideo ? (cameraOff ? <VideoCameraAddOutlined /> : <VideoCameraOutlined />) : <VideoCameraAddOutlined />}
              onClick={onToggleCamera}
              title={sendingVideo ? (cameraOff ? "Camera on" : "Camera off") : "Turn on camera"}
            />
            {cameras.length > 1 && sendingVideo && !cameraOff && !sharingScreen && (
              <Button shape="circle" size="large" icon={<FlipCameraIcon />} onClick={onFlipCamera} title="Flip camera (front / back)" />
            )}
            <Button
              shape="circle"
              size="large"
              type={sharingScreen ? "primary" : "default"}
              icon={<FundProjectionScreenOutlined />}
              onClick={onShareScreen}
              title={sharingScreen ? "Stop sharing your screen" : "Share your screen"}
            />
            {sharingScreen && onToggleAnnotation && (
              <Button
                shape="circle"
                size="large"
                type={annotationAllowed ? "primary" : "default"}
                icon={<EditOutlined />}
                onClick={onToggleAnnotation}
                title={annotationAllowed ? "Stop letting others annotate your screen" : "Allow others to annotate your screen"}
              />
            )}
            {onOpenWhiteboard && (
              <Button shape="circle" size="large" icon={<HighlightOutlined />} onClick={onOpenWhiteboard} title="Open the shared whiteboard" />
            )}
            <Dropdown menu={deviceMenu} trigger={["click"]} placement="top" disabled={!mics.length && !cameras.length}>
              <Button shape="circle" size="large" icon={<SettingOutlined />} title="Devices (microphone / camera)" />
            </Dropdown>
            <Button shape="circle" size="large" danger type="primary" icon={<PhoneOutlined rotate={135} />} onClick={onHangup} title="Hang up" />
          </>
        )}
      </div>
    </div>
  );
}

// CallQualityChip renders a small live link-health indicator: a coloured dot graded
// on packet loss + round-trip time, or a "reconnecting" spinner while the peer
// connection re-establishes (the ICE-restart/drop-timer riding out a blip).
function CallQualityChip({ q }: { readonly q: CallQuality }) {
  if (q.state === "reconnecting") {
    return (
      <span className="call-quality call-quality-reconnecting" title="The connection dropped — trying to recover">
        ⟳ Reconnecting…
      </span>
    );
  }
  const label = { good: "Good", fair: "Fair", poor: "Poor" }[q.state];
  const detail = [q.loss != null ? `${q.loss.toFixed(1)}% loss` : "", q.rtt ? `${Math.round(q.rtt)} ms` : ""].filter(Boolean).join(" · ");
  return (
    <span className={`call-quality call-quality-${q.state}`} title={`Connection: ${label}${detail ? ` (${detail})` : ""}`}>
      <span className="call-quality-dot" /> {label}
    </span>
  );
}
